import React, { useState, useEffect } from "react";
import { Table, Empty, Popconfirm, Dropdown, Modal, Form, Input, Select, DatePicker, Button, Tag, Typography, TreeSelect, Tooltip, Row, Col, Checkbox } from "antd";
import moment from "moment";
import { TrainingAPI } from "../../api/TrainingAPI";
import "../../Styles/styles-InjuryTable.css";
import { formattedPlayerName } from "../../Utils/name_utils";
import { useSelector, useDispatch } from "react-redux";
import { ReloadOutlined } from "@ant-design/icons";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { EditOutlined, DeleteOutlined, CloseCircleFilled } from "@ant-design/icons";
import "../../Styles/styles-TreatmentLog.css";
import { 
  PermissionedButton, 
  PermissionedPopConfirm 
} from "../PermissionedComponents";
import PermissionViewable from "../PermissionedComponents/PermissionViewable";
import PieChart from "../Charts/PieChart";

const { Option } = Select;

const getFormattedPlayerName = (teamName, player) => {
  return `${formattedPlayerName(teamName,`${player.first_name} ${player.last_name}`)} #${player.number}`
}

// Helper functions for pie chart data calculation
const calculateTreatmentDistribution = (treatmentLogs, treatment_types) => {
  const treatmentCounts = {};
  let totalTreatments = 0;

  treatmentLogs.forEach(log => {
    if (log.treatments && Array.isArray(log.treatments)) {
      log.treatments.forEach(treatment => {
        treatmentCounts[treatment] = (treatmentCounts[treatment] || 0) + 1;
        totalTreatments++;
      });
    }
  });

  return Object.entries(treatmentCounts).map(([treatment, count]) => {
    const treatmentType = treatment_types.find(type => type.name === treatment);
    return {
      name: treatment,
      y: count,
      color: treatmentType?.color || '#CCCCCC'
    };
  });
};

const calculateLocationDistribution = (treatmentLogs, teamColors = []) => {
  const locationCounts = {};

  treatmentLogs.forEach(log => {
    if (log.location && Array.isArray(log.location)) {
      log.location.forEach(location => {
        locationCounts[location] = (locationCounts[location] || 0) + 1;
      });
    }
  });

  const defaultColors = ['#89f5ff', '#8884d8', '#a7ff81', '#ffaea3', '#4797ff', '#f526fA'];
  // Only use first 5 team colors to avoid dummy 6th item
  const validTeamColors = teamColors.length > 0 ? teamColors.slice(0, 5) : [];
  const colorsToUse = validTeamColors.length > 0 ? validTeamColors : defaultColors;

  return Object.entries(locationCounts).map(([location, count], index) => ({
    name: location,
    y: count,
    color: colorsToUse[index % colorsToUse.length]
  }));
};

const calculateTreatmentTimeDistribution = (treatmentLogs, teamColors = []) => {
  const timeCounts = {};

  treatmentLogs.forEach(log => {
    const time = log.treatment_application_time || 'Unknown';
    timeCounts[time] = (timeCounts[time] || 0) + 1;
  });

  const defaultColors = ['#89f5ff', '#8884d8', '#a7ff81', '#ffaea3', '#4797ff', '#f526fA'];
  // Only use first 5 team colors to avoid dummy 6th item
  const validTeamColors = teamColors.length > 0 ? teamColors.slice(0, 5) : [];
  const colorsToUse = validTeamColors.length > 0 ? validTeamColors : defaultColors;

  return Object.entries(timeCounts).map(([time, count], index) => ({
    name: time,
    y: count,
    color: colorsToUse[index % colorsToUse.length]
  }));
};

const TreatmentInputForm = (props) => {
	const {
		misc: { 
            treatment_types,
            injury_location_tree,
            treatment_applied_time,
        },
        players,
        info: { display_name: teamName}
	} = useSelector(({ team }) => team);

  const [form] = Form.useForm();
  const allValues = Form.useWatch([], form);
  const mode = props?.inputData?.id ? "edit" : "add";
  const log_id = props?.inputData?.treatment_log_id || null;
  const trainingAPI = new TrainingAPI();

  useEffect(() => {
    form.setFieldsValue({
      date: props.inputData?.date ? moment(props.inputData.date, "YYYY-MM-DD") : null,
      player_name: props.inputData?.player || null,
      treatments: props.inputData?.treatments || [],
      location: props.inputData?.location || [],
      treatment_application_time: props.inputData?.treatment_application_time || null,
      notes: props.inputData?.notes || ""
    });
  }, [props.inputData]);


  const modifiedInjuryLocationTree = injury_location_tree.map(
    (item) => {

      let modified = { value: item.key, title: item.key }
      if (item.children && item.children.length > 0) {
        modified.children = item.children.map((child) => ({
          value: child.key,
          title: child.key,
        }));
      }
      return modified;
    });


  const onSubmit = (values) => {

    const payload = {
      ...values,
        date: values.date?.format("YYYY-MM-DD"),
        player: values.player_name,
    }

    if (mode === "add"){
      trainingAPI.addTreatmentLog(payload)
      .then(() => {
          message.success("Treatment log added successfully");
          props?.setIsFormOpen(false);
      })
      .catch((error) => {
          console.log(error);
          message.error("Error adding treatment log");
      });
    } else if (mode === "edit"){
      trainingAPI.updateTreatmentLog(log_id, payload)
      .then(() => {
          message.success("Treatment log added successfully");
          props?.setIsFormOpen(false);
      })
      .catch((error) => {
          console.log(error);
          message.error("Error adding treatment log");
      });
    } else {
      message.error("Unknown action");
    }
  }

  return (
  <>

        <Form
            form={form}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 20 }}
            layout="horizontal"
            style={{ maxWidth: 600 }}
            onFinish={onSubmit}
      >

        <Form.Item 
            name="date" 
            label="Date" 
            rules={[{ required: true, message: "Please select a date" }]}
            >
            <DatePicker format="YYYY-MM-DD" style={{ borderRadius: "5px" }} />
        </Form.Item>
        <Form.Item 
            name="player_name" 
            label="Player" 
            rules={[{ required: true, message: "Please select a player" }]}
            >
            <Select
                showSearch
                allowClear
                style={{ width: 250 }}
                placeholder="Choose Player"
                filterOption={(input, option) =>
                    option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
            >
                {players?.map(
                  (player, i) => ({
                      key: i,
                      value: player.player_id,
                      label: `${formattedPlayerName(teamName,`${player.first_name} ${player.last_name}`)} #${player.number}`
                }))
                .filter(item => item.label !== "Unknown")
                .map((player) => (
                    <Option key={player.key} value={player.value}>
                        {player.label}
                    </Option>
                ))}  
            </Select>
            </Form.Item>


            <Form.Item 
                name="treatments" 
                label="Treatments" 
                rules={[{ required: true, message: "Please select at least one treatment" }]}
                >
                <Select
                    showSearch
                    mode="multiple"
                    optionFilterProp="label"
                    allowClear
                    placeholder="Choose Treatments"
                    style={{ width: 250 }}
                    options={treatment_types.map((treatment) => ({
                      label: treatment.name,
                      value: treatment.name,
                    }))}
                    filterOption={(input, option) =>option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                />
            </Form.Item>

            <Form.Item 
                name="location" 
                label="Location" 
                rules={[{ required: true, message: "Please select a location" }]}
                >
              <TreeSelect
                showSearch
                treeData={modifiedInjuryLocationTree}
                style={{ width: 250 }}
                multiple
                styles={{ popup: { root: { maxHeight: 400, overflow: 'auto' } }}}
                placeholder="Please select"
                allowClear
                />

            </Form.Item>

            <Form.Item 
                name="treatment_application_time" 
                label="When Applied" 
                rules={[{ required: true, message: "Please select when the treatment was applied" }]}
                >
                <Select
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    placeholder="When Applied"
                    style={{ width: 250 }}
                    options={treatment_applied_time.map((treatment) => ({
                    label: (
                        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {treatment.name}
                        </span>
                    ),
                    value: treatment.name,
                    }))}
                    filterOption={(input, option) =>option?.value?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                />
            </Form.Item>

            <Form.Item 
                name="notes" 
                label="Notes"
                >
                <Input.TextArea 
                    rows={4}
                    placeholder="Enter any relevant notes"
                    style={{ borderRadius: "5px" }}
                />
            </Form.Item>

            <Form.Item>
                <PermissionedButton 
                    type="primary" 
                    htmlType="submit"
                    permissionCheck={mode === "add" ? "AddTreatmentLogs" : "EditTreatmentLogs"}
                >
                Save Treatments
                </PermissionedButton>
            </Form.Item>

      </Form>
  </>
  )
}

export const TreatmentLog = (props) => {

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [inputData, setInputData] = useState({});
  const [allTreatmentLogs, setAllTreatmentLogs] = useState([]);
  const [showChartLabels, setShowChartLabels] = useState(true);
  const trainingAPI = new TrainingAPI();
  const dateFormat = "YYYY-MM-DD";

  const [dateRange, setDateRange] = useState([
    moment().subtract(2, "week").format(dateFormat),
    moment().format(dateFormat),
    ]);

    const [filters, setFilters] = useState({});
  
    const {
      players,
      positions,
      playerGroups,
      misc: { treatment_types,
        user_permissions:userPermissions
       },
      info,
      info: { display_name: teamName}
    } = useSelector(({ team }) => team);

  useEffect(() => { getAllTreatmentLogs() }, [isFormOpen]);

  const getTreatmentColorByName = (treatment) => {
    const treatmentType = treatment_types.find((type) => type.name === treatment);
    if (treatmentType) {
      return treatmentType.color || "#CCCCCC"; // Default color if not specified
    }
    return "#CCCCCC"; // Default color for unknown treatments
  }

  const applyFilters = (treatmentLogs) => {
    const activeFilters = { ...filters };
  
    // Clean up empty filters
    Object.keys(activeFilters).forEach((key) => {
      if (!activeFilters[key] || activeFilters[key]?.length === 0) {
        delete activeFilters[key];
      }
    });
  
    if (Object.keys(activeFilters).length === 0) {
      return treatmentLogs;
    }
  
    return treatmentLogs
      .map((log) => {
        const player = players.find((p) => p.player_id === log.player);
  
        // Player filter
        if (activeFilters.player && activeFilters.player !== log.player) return null;
  
        // Position filter
        if (
          activeFilters.positions?.length > 0 &&
          (!player || !activeFilters.positions.includes(player.position))
        ) {
          return null;
        }
  
        // Group filter
        if (activeFilters.groups?.length > 0) {
          const matchesGroup = player?.player_groups?.some(group =>
            activeFilters.groups.includes(group)
          );
          if (!matchesGroup) return null;
        }
  
        // Treatment filter
        let filteredTreatments = log.treatments;
        if (activeFilters.treatments?.length > 0) {
          filteredTreatments = log.treatments.filter((t) =>
            activeFilters.treatments.includes(t)
          );
          if (filteredTreatments.length === 0) return null;
        }
  
        // Location filter
        let filteredLocations = log.location;
        if (activeFilters.locations?.length > 0) {
          filteredLocations = log.location?.filter((loc) =>
            activeFilters.locations.includes(loc)
          ) || [];
          if (filteredLocations.length === 0) return null;
        }

        // treatment_application_time filter
        let treatmentApplicationTime = log.treatment_application_time || "";
        if (activeFilters.treatment_application_time && !activeFilters.treatment_application_time.includes(treatmentApplicationTime)) {
          return null;
        }
  
        return {
          ...log,
          treatments: filteredTreatments,
          location: filteredLocations,
        };
      })
      .filter(Boolean);
  };
  
  

  const genTreatmentTree = (treatmentLogs) => {

    const filtered = applyFilters(treatmentLogs);

    // Calculate summary data
    const uniqueDates = [...new Set(filtered.map(log => log.date))];
    const uniquePlayers = [...new Set(filtered.map(log => log.player))];
    const allTreatments = filtered.flatMap(log => log.treatments || []);
    const uniqueTreatments = [...new Set(allTreatments)];
    const allLocations = filtered.flatMap(log => log.location || []);
    const uniqueLocations = [...new Set(allLocations)];
    const allWhenApplied = filtered.map(log => log.treatment_application_time || 'Unknown');
    const uniqueWhenApplied = [...new Set(allWhenApplied)];

    // Create summary row
    const summaryRow = {
      key: 'summary',
      date: <span style={{fontWeight: 600}}>Total ({uniqueDates.length})</span>,
      player: <span style={{fontWeight: 600}}>{uniquePlayers.length}</span>,
      treatment_count: <span style={{fontWeight: 600}}>{allTreatments.length} ({uniqueTreatments.length})</span>,
      formatted_treatment_list: <span style={{fontWeight: 600}}>{uniqueTreatments.length}</span>,
      formatted_locations: <span style={{fontWeight: 600}}>{uniqueLocations.length}</span>,
      treatment_application_time: <span style={{fontWeight: 600}}>{uniqueWhenApplied.length}</span>,
      notes: '',
      actions: '',
      total_count: allTreatments.length,
      unique_treatments_on_curr_date: uniqueTreatments,
      unique_locations_on_curr_date: uniqueLocations.length,
      raw_treatment_list: uniqueTreatments,
      raw_location_list: uniqueLocations,
    };

    const treatmentTree = uniqueDates.map(date => {
    const logsForDate = filtered.filter(log => log.date === date);

        let treatment_info_for_curr_day = {}

        const subData = logsForDate.map(log => {

            log.treatments.forEach(treatment => {
                if (!treatment_info_for_curr_day[treatment]) {
                    treatment_info_for_curr_day[treatment] = {count: 0, locations: new Set()};
                }
                treatment_info_for_curr_day[treatment].count += 1;
                if (log.location) {
                  log.location.forEach(x => treatment_info_for_curr_day[treatment].locations.add(x));
                }
            })

            
            return {
                key: log.id,
                date: log.date,
                player_name: log.player_name,
                player: log.player_name,
                treatment_count: log.treatments.length,
                
                raw_treatment_list: log.treatments || [],
                formatted_treatment_list: log.treatments.map((treatment, idx) => (
                  <Tag className="treatment-tag" key={idx} style={{ margin: 2, backgroundColor: getTreatmentColorByName(treatment) }}>{treatment}</Tag>
                )),

                raw_location_list: log.location || [],
                unique_locations_on_curr_date: new Set(log.location || []).size,
                formatted_locations: (log.location || []).map((loc, idx) => (
                  <Tag key={idx} color="geekblue" style={{ margin: 2 }}> {loc} </Tag>
                )),

                treatment_application_time: log.treatment_application_time || "",
                location: log.location || "",
                notes: log.notes || "",
                actions: (
                  <div style={{ display: "flex", justifySelf:'center', gap: 4}}>
                    <PermissionedButton
                      icon={<EditOutlined />}
                      shape="circle"
                      type="primary"
                      size="small"
                      ghost
                      onClick={() => handleEdit(log.id)}
                      permissionCheck="EditTreatmentLogs"
                    />
        
                    <PermissionedPopConfirm
                        placement="bottom"
                        title="Are you sure you want to delete this treatment log?"
                        onConfirm={() => handleDelete(log.id)}
                        okText="Yes"
                        cancelText="No"
                        permissionCheck="DeleteTreatmentLogs"
                        >
                        <Button 
                            icon={<DeleteOutlined />} 
                            shape="circle" 
                            size="small"
                            danger 
                            ghost 
                        />                    
                    </PermissionedPopConfirm>
                  </div>
                )
            };
        });

        let player_count_for_current_date = new Set(subData.map(item => item.player_name)).size;
        let unique_treatments_on_curr_date = treatment_info_for_curr_day ? Object.keys(treatment_info_for_curr_day) : [];
        let total_treatments_on_curr_date = subData.reduce((acc, item) => acc + item.treatment_count, 0);

        let unique_locations_on_curr_date = []
        if (Object.keys(treatment_info_for_curr_day).length > 0) {
          let locations = Object.keys(treatment_info_for_curr_day).map(treatment => treatment_info_for_curr_day[treatment].locations).flat();
          let all_locations = [];
          locations.forEach(loc => all_locations.push(...loc));
          unique_locations_on_curr_date = new Set(all_locations);
        }

        const treatment_count_with_tooltip =(
        <Tooltip title={<>Total: {total_treatments_on_curr_date} | Unique: {unique_treatments_on_curr_date?.length}</>}>
          {total_treatments_on_curr_date} ({unique_treatments_on_curr_date.length})
        </Tooltip>
      )


        return {
            key: date,
            date: date,
            player: player_count_for_current_date,
            treatment_count: treatment_count_with_tooltip,
            formatted_treatment_list: unique_treatments_on_curr_date.map((treatment, idx) => (
              <Tag className="treatment-tag" key={idx} style={{margin: 2, backgroundColor: getTreatmentColorByName(treatment)}}>
                {treatment} ({treatment_info_for_curr_day[treatment]?.count || 0})
              </Tag>
            )),
            formatted_locations: unique_locations_on_curr_date?.size || '-',
            unique_locations_on_curr_date: unique_locations_on_curr_date.size || 0,
            treatment_application_time: [...new Set(subData.map(item => item.treatment_application_time))].length,
            notes: "",
            actions: "",

            total_count: total_treatments_on_curr_date,

            subData: subData,
            unique_treatments_on_curr_date: unique_treatments_on_curr_date,
            children: subData
        };
    }
    );

    return [summaryRow, ...treatmentTree];
    }

  const getAllTreatmentLogs = () => {
    if (!userPermissions?.ViewTreatmentLogs) return;
    trainingAPI.getAllTreatmentLogs({start_date: dateRange[0], end_date: dateRange[1]})
      .then((data) => {
        setAllTreatmentLogs(data);
      }
      ).catch((error) => {
        console.log(error);
        message.error("Error fetching treatment logs");
      });
  }

  const deleteTreatmentLog = (logId) => {
    trainingAPI.deleteTreatmentLog(logId)
      .then(() => {
        message.success("Treatment log deleted successfully");
        getAllTreatmentLogs();
      })
      .catch((error) => {
        console.log(error);
        message.error("Error deleting treatment log");
      });
  }

  const getTreatmentLogById = (logId) => {
    return allTreatmentLogs.find((log) => log.id === logId);
  }

  const handleEdit = (logId) => {
    const logData = getTreatmentLogById(logId);
    if (logData) {
        setInputData({...logData, treatment_log_id: logId});
        setIsFormOpen(true);
    } else {
      message.error("Treatment log not found");
    }
  }

  const handleDelete = async (logId) => {
    deleteTreatmentLog(logId);
    getAllTreatmentLogs();
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 150,
      sorter: {
        compare: (a, b) => new Date(a.date) - new Date(b.date),
        multiple: 1,
      },
    },
    {
      'title': 'Player',
      'dataIndex': 'player',
      'key': 'player',
    },
    {
      title: "Treatment Count",
      dataIndex: "treatment_count",
      key: "treatment_count",
      width: 10,
      sorter: {
        compare: (a, b) => a.total_count - b.total_count,
        multiple: 1,
      },
    },
    {
      title: "Treatments",
      dataIndex: "formatted_treatment_list",
      key: "formatted_treatment_list",
      width: 200,
      sorter: {
        compare: (a, b) => (a.unique_treatments_on_curr_date?.length || 0) - (b.unique_treatments_on_curr_date?.length || 0),
        multiple: 1,
      },
      filters: treatment_types.map((treatment) => ({
        text: treatment.name,
        value: treatment.name,
      })),
      onFilter: () => true, // required to show the filter UI, but doesn't do real filtering
    },
      {
        'title': 'Location',
        'dataIndex': 'formatted_locations',
        'key': 'formatted_locations',
        onFilter: () => true, // just to make the UI appear — real filtering is handled in applyFilters
        filters: Array.from(new Set(allTreatmentLogs.flatMap(log => log.location || []))).map(location => ({
          text: location,
          value: location,
        })),
        sorter: {
          compare: (a, b) => a.unique_locations_on_curr_date - b.unique_locations_on_curr_date,
          multiple: 1,
        }
      },
      {
        'title': 'When Applied',
        'dataIndex': 'treatment_application_time',
        'key': 'treatment_application_time',
        onFilter: () => true, // just to make the UI appear — real filtering is handled in applyFilters
        filters: Array.from(new Set(allTreatmentLogs.map(log => log.treatment_application_time))).map(time => ({
          text: time,
          value: time,
        })),
        sorter: {
          compare: (a, b) => a?.treatment_application_time?.localeCompare(b?.treatment_application_time),
          multiple: 1,
        }
      },
      {
        'title': 'Notes',
        'dataIndex': 'notes',
        'key': 'notes',
        render: (text) => (
          <Typography.Text 
            style={{ maxWidth: '100px' }}
            ellipsis={{ tooltip: text }}
          >
            {text}
          </Typography.Text>
        )
      },
      {
        'title': 'Actions',
        'dataIndex': 'actions',
        'key': 'actions',
      },
      
  ];

  return (
    <div
      style={{
        margin: "24px 0px",
      }}
    >
      <div style={{ display: "flex", gap: 16 }}>

      <PermissionedButton 
        type="primary"
        onClick={() => setIsFormOpen(true)}
        permissionCheck="AddTreatmentLogs"
      >
        Add Treatment
      </PermissionedButton>
      <Button 
        variant="outlined"
        onClick={() => {getAllTreatmentLogs(); message.success("Treatment logs refreshed")}}
        style={{right: 0, position: 'absolute'}}
        disabled={!userPermissions?.ViewTreatmentLogs}
        icon={<ReloadOutlined />}
        />
      </div>

      <Modal 
        title="New Treatment" 
        open={isFormOpen}
        onClose={() => {setInputData({}); setIsFormOpen(false)}}
        onCancel={() => {setInputData({}); setIsFormOpen(false)}}
        destroyOnClose={true}
        footer={null}
        >
          <TreatmentInputForm 
            setIsFormOpen={setIsFormOpen} 
            inputData={inputData}
          />
        </Modal>
        {!userPermissions?.ViewTreatmentLogs? <div style={{marginTop: '10px'}}/>:null}
        <PermissionViewable 
        permissionCheck="ViewTreatmentLogs"
        permissionMessage="You don't have permission to view treatment logs"
      >
      <div className="filter-container">

        <Typography.Text style={{fontSize: '15px'}}>Filters: </Typography.Text>

        <DatePicker.RangePicker
            style={{ borderRadius: "5px" }}
            format={dateFormat}
            value={[
              moment(dateRange[0], dateFormat),
              moment(dateRange[1], dateFormat),
            ]}
            onChange={(dates, dateStrings) => setDateRange(dateStrings)}
            />
        
        <Select
          showSearch
          allowClear
          style={{ width: 175 }}
          placeholder="Choose Player"
          onChange={(value) => {setFilters({...filters, player: value})}}
          value={filters?.player || null}
          filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
      >
          {players?.map(
            (player, i) => ({
                key: i,
                value: player.player_id,
                label: getFormattedPlayerName(teamName, player)
          }))
          .filter(item => item.label !== "Unknown")
          .map((player) => (
              <Option key={player.key} value={player.value}>
                  {player.label}
              </Option>
          ))}  
          </Select>

                  <Select
            showSearch
            allowClear
            placeholder="All Positions"
            style={{ minWidth: 150 }}
            options={positions.map((position) => ({
              label: position,
              value: position,
            }))}
            onChange={(value) => {setFilters({...filters, positions: value})}}   
            mode="multiple"           
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={filters?.positions || []}
          />
          <Select
            showSearch
            allowClear
            placeholder="All Groups"
            style={{ minWidth: 150 }}
            options={playerGroups.map((group) => ({
              label: group.name,
              value: group.player_group_id,
            }))}
            onChange={(value) => {setFilters({...filters, groups: value})}}   
            mode="multiple"           
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={filters?.groups || []}
          />

      </div>

      <PermissionViewable 
        permissionCheck="ViewTreatmentLogs"
      >
        {/* Pie Charts Row */}
        <Row gutter={[16, 16]} style={{ margin: '20px 0' }}>
          <Col span={8}>
            <PieChart
              showDataLabelLines={showChartLabels}
              showDataLabels={showChartLabels}
              showLegend={false}
              size={showChartLabels ? '60%' : '90%'}
              options={{
                title: {
                  text: 'Treatments',
                  style: {
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }
                },
                series: [{
                  name: 'Count',
                  data: calculateTreatmentDistribution(applyFilters(allTreatmentLogs), treatment_types)
                }],
                tooltip: {
                  pointFormat: '{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
                }
              }}
              height="300px"
            />
          </Col>
          <Col span={8}>
            <PieChart
              showDataLabelLines={showChartLabels}
              showDataLabels={showChartLabels}
              showLegend={false}
              size={showChartLabels ? '60%' : '90%'}
              options={{
                title: {
                  text: 'Locations',
                  style: {
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }
                },
                series: [{
                  name: 'Count',
                  data: calculateLocationDistribution(applyFilters(allTreatmentLogs), info?.team_colors?.all || [])
                }],
                tooltip: {
                  pointFormat: '{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
                }
              }}
              height="300px"
            />
          </Col>
          <Col span={8}>
            <PieChart
              showDataLabelLines={showChartLabels}
              showDataLabels={showChartLabels}
              showLegend={false}
              size={showChartLabels ? '60%' : '90%'}
              options={{
                title: {
                  text: 'When Applied',
                  style: {
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }
                },
                series: [{
                  name: 'Count',
                  data: calculateTreatmentTimeDistribution(applyFilters(allTreatmentLogs), info?.team_colors?.all || [])
                }],
                tooltip: {
                  pointFormat: '{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
                }
              }}
              height="300px"
            />
          </Col>
        </Row>
        
        {/* Chart Labels Control */}
        <div style={{ margin: '-10px 0 16px 0', textAlign: 'center' }}>
          <Checkbox 
            checked={showChartLabels}
            onChange={(e) => setShowChartLabels(e.target.checked)}
            style={{ 
              color: '#888888',
              '--ant-primary-color': '#888888'
            }}
          >
            <span style={{ color: '#888888' }}>Show Chart Labels</span>
          </Checkbox>
        </div>
      </PermissionViewable>


        <Table
            className="custom-table-container"
            rowClassName={(record) => {
              if (record.key === 'summary') {
                return "treatment-table-row treatment-summary-row";
              }
              return "treatment-table-row";
            }}
            size="small"
            columns={columns}
            dataSource={genTreatmentTree(allTreatmentLogs)}
            locale={!userPermissions?.ViewTreatmentLogs ? <Empty description="You do not have permission to view this table." /> :null }
            onChange={(pagination, filters, sorter) => {
              setFilters((prev) => ({
                ...prev,
                treatments: filters.formatted_treatment_list || [],
                locations: filters.formatted_locations || [],
                treatment_application_time: filters.treatment_application_time || null,
              }));
            }}
            rowKey="key"
        />
      </PermissionViewable>
    </div>
  );
};
